import matrix from './featureFlags.matrix.json'
import { AppEnv, ENV } from './env'

/**
 * BUILD-TIME FEATURE FLAGS
 *
 * Everything resolves from src/environment/featureFlags.matrix.json against the
 * NODE_ENV baked in by webpack's DefinePlugin. There is no network call, no
 * React context and no async step, so a flag can never read false-then-true on
 * first render and a failed request can never pin every flag off. (Both were
 * real defects in the S3-backed provider this replaces - see git history for
 * src/hooks/FeatureFlagContext.tsx.)
 *
 * Changing a flag means editing the JSON and rebuilding. Confirm what a build
 * will contain with `npm run flags -- --env=staging` before kicking it off.
 */

/** SCREAMING_SNAKE product keys from the matrix. */
export type ProductFlag = keyof typeof matrix.products

interface ProductDef {
  menuId: string
  description: string
  parent?: ProductFlag
  envs: Partial<Record<AppEnv, boolean>>
  regionOverrides?: Record<string, Partial<Record<AppEnv, boolean>>>
}

const PRODUCTS = matrix.products as unknown as Record<ProductFlag, ProductDef>
const PRODUCT_KEYS = Object.keys(PRODUCTS) as ProductFlag[]
const FALLBACK_REGION: string = matrix.fallbackRegion

/** menu id -> owning product key. Built once at module load. */
const MENU_ID_TO_PRODUCT: Readonly<Record<string, ProductFlag>> = Object.freeze(
  PRODUCT_KEYS.reduce((acc, key) => {
    acc[PRODUCTS[key].menuId] = key
    return acc
  }, {} as Record<string, ProductFlag>)
)

/**
 * One product's own value, ignoring ancestors.
 * regionOverrides[region][env] -> envs[env] -> false (fail closed).
 */
function ownValue(key: ProductFlag, region: string): boolean {
  const def = PRODUCTS[key]
  const override = def.regionOverrides?.[region]?.[ENV]
  if (typeof override === 'boolean') return override
  return def.envs[ENV] === true
}

/**
 * A product is enabled only when it and every ancestor are enabled, so flipping
 * a group key (e.g. WET_DOG_WEATHER_TILES) switches all of its children off.
 * `parent` mirrors the subMenu tree in SideNavItems.tsx.
 */
function resolve(key: ProductFlag, region: string): boolean {
  const seen = new Set<ProductFlag>()
  let cursor: ProductFlag | undefined = key
  while (cursor) {
    if (seen.has(cursor)) break // malformed parent cycle; validated at build time
    seen.add(cursor)
    if (!ownValue(cursor, region)) return false
    cursor = PRODUCTS[cursor].parent
  }
  return true
}

/**
 * Product key -> its menuId, so a hand-written gate can say
 * `MENU_ID.ALERTING_MONITORS` instead of the string `'alerts-menu'`.
 *
 * Worth the indirection because resolution is DEFAULT-OPEN: a typo'd literal
 * resolves to `true` and the gate silently does nothing. That is the one way
 * this system can lie to you. Nav-driven call sites keep using `item.id` /
 * `MenuLabels.*` - those ids come from the tree and cannot drift.
 */
export const MENU_ID: Readonly<Record<ProductFlag, string>> = Object.freeze(
  PRODUCT_KEYS.reduce((acc, key) => {
    acc[key] = PRODUCTS[key].menuId
    return acc
  }, {} as Record<ProductFlag, string>)
)

/** Product key owning a side-nav id, or undefined when the id is not in the matrix. */
export function getProductFlag(
  menuId?: string | null
): ProductFlag | undefined {
  return menuId ? MENU_ID_TO_PRODUCT[menuId] : undefined
}

/**
 * Is the product behind this side-nav id enabled in this build?
 *
 * `region` is the string from getRegionBasedServices(location).region - a
 * separate axis from the menu id, because every side-nav id is region-neutral
 * ('aviation-ceiling', never 'aviation-ceiling-ca').
 *
 * DEFAULT-OPEN: a menu id absent from the matrix is enabled. Forgetting to add
 * a new id therefore cannot silently break a working product.
 */
export function isProductEnabled(
  menuId?: string | null,
  region?: string | null
): boolean {
  const key = getProductFlag(menuId)
  if (key === undefined) return true
  return resolve(key, region || FALLBACK_REGION)
}

/** Every product key that is off in this build, ignoring region overrides. */
export function disabledProducts(region?: string | null): ProductFlag[] {
  const r = region || FALLBACK_REGION
  return PRODUCT_KEYS.filter((key) => !resolve(key, r))
}

/** One-line startup banner, mirroring what the build printed for Jenkins. */
export const FEATURE_FLAG_SUMMARY: string = (() => {
  const off = disabledProducts()
  return (
    `[feature-flags] env=${ENV} region=${FALLBACK_REGION} ` +
    `${PRODUCT_KEYS.length - off.length} ON / ${off.length} OFF` +
    (off.length ? ` OFF=[${off.join(', ')}]` : '')
  )
})()
